/**
 * The SCREEN's wiring: one read, and the two things every tab draws from it.
 *
 * The header read carries who the client is and what plan they are on. The top bar prints the
 * first, the plan card and the Kalendarz link need the second, and both are on screen whichever tab
 * is open — so this read is the screen's, and it is the only one that is.
 *
 * ── The summary column's reads are NOT here, and that is the point ────────────────────────────
 * They were, until 2 September 2026, and the cost was three requests per visit on the five tabs
 * that draw none of them: `/note`, `/goal` and `/consistency` (plus `/compliance` on a self-paced
 * client) fired on Trendy, Pomiary, Rekordy, Ankiety and Kalendarz, and again on every window
 * refocus. The column stands on Przegląd alone since KANON v3, so its reads live in the component
 * that draws it — `useSummaryColumn` — exactly like every tab's own. An unmounted surface has no
 * query, by construction rather than by a flag.
 *
 * Both hooks call `clientDetailQueryOptions`, and that costs nothing: one key, one cache entry, one
 * request. It is also what stops the two disagreeing about which plan the client is on.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { isGone } from '~/shared/api/mutator';

import { clientDetailQueryOptions } from '../api/clientDetailQueries';
import { toDetailSource } from '../api/clientDetailSource';
import {
  buildSummaryColumn,
  type IdentityView,
  type PlanView,
} from '../model/summaryColumn';

export interface ClientDetailState {
  /**
   * Who the client is, for the top bar — `null` while the header read is open. The bar reserves the
   * avatar's box and prints a loading word in the `<h1>` meanwhile, so absence is a state it draws
   * rather than a reason to draw nothing.
   */
  readonly identity: IdentityView | null;
  /** The active plan, or `null` for a client on none. The screen needs its id and its name. */
  readonly plan: PlanView | null;
  /** The client does not exist, or is not this trainer's. A dead end with no Retry. */
  readonly notFound: boolean;
  /** Something transient. The screen offers a retry. */
  readonly failed: boolean;
  readonly refetch: () => void;
}

export function useClientDetail(clientId: number): ClientDetailState {
  const { t, i18n } = useTranslation();

  const detail = useQuery(clientDetailQueryOptions(clientId));

  /**
   * Built through the column's own projection rather than beside it.
   *
   * `identity` and `plan` depend on the header read and on nothing else, so the four `null`s below
   * cannot change either of them — and going through one function is what stops the top bar and the
   * plan card from ever spelling the same client, or the same plan's category, two ways.
   */
  const view = buildSummaryColumn(
    {
      detail: toDetailSource(detail.data),
      note: null,
      consistency: null,
      standing: null,
      goal: null,
    },
    { t, locale: i18n.language },
  );

  return {
    identity: view.identity,
    plan: view.plan,
    notFound: detail.isError && isGone(detail.error),
    failed: detail.isError && !isGone(detail.error),
    refetch: () => {
      void detail.refetch();
    },
  };
}
